import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Banknote, CheckCircle2 } from "lucide-react";
import api, { fmtErr, peso } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { NeedTenant } from "../components/layout/AppShell";

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function PayrollPage() {
  const { user, activeTenantId } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: today.slice(0, 8) + "01", to: today });
  const [rows, setRows] = useState(null);
  const [deductions, setDeductions] = useState({});
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [statement, setStatement] = useState(null);

  const loadPayroll = useCallback(async () => {
    try {
      const { data } = await api.get("/payroll", { params: { from_date: range.from, to_date: range.to } });
      setRows(data.rows);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [range]);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    loadPayroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPayroll, activeTenantId]);

  const loadStatement = useCallback(async () => {
    try {
      const { data } = await api.get("/finance/statement", { params: { year: Number(year) } });
      setStatement(data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, [year]);
  useEffect(() => {
    if (user.role === "system_admin" && !activeTenantId) return;
    loadStatement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadStatement, activeTenantId]);

  const pay = async (r) => {
    try {
      await api.post("/payroll/payout", {
        user_id: r.user_id, from_date: range.from, to_date: range.to,
        deductions: Number(deductions[r.user_id]) || 0,
      });
      toast.success(`Paid ${r.name} — recorded as Payroll expense`);
      loadPayroll();
      loadStatement();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (user.role === "system_admin" && !activeTenantId) return <NeedTenant />;

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Payroll &amp; Statements</h1>
        <p className="text-sm text-slate-500">Attendance-based pay computation · payouts auto-record as expenditures</p>
      </div>

      <Tabs defaultValue="payroll">
        <TabsList>
          <TabsTrigger value="payroll" data-testid="payroll-tab">Payroll</TabsTrigger>
          <TabsTrigger value="statement" data-testid="statement-tab">Income Statement</TabsTrigger>
        </TabsList>

        <TabsContent value="payroll" className="mt-4 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-end gap-3">
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" className="w-40 h-9" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} data-testid="payroll-from-date" /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" className="w-40 h-9" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} data-testid="payroll-to-date" /></div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
            <Table data-testid="payroll-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Staff</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Shifts</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Rate/hr</TableHead>
                  <TableHead className="text-right">Gross Pay</TableHead>
                  <TableHead className="text-right">Deductions</TableHead>
                  <TableHead className="text-right">Net Pay</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows || []).map((r) => {
                  const ded = Number(deductions[r.user_id]) || 0;
                  const net = r.gross_pay - ded;
                  return (
                    <TableRow key={r.user_id} data-testid={`payroll-row-${r.user_id}`}>
                      <TableCell className="font-medium text-slate-900">{r.name}</TableCell>
                      <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize">{r.role}</span></TableCell>
                      <TableCell className="text-right font-mono text-sm">{r.shifts}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{r.hours}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{peso(r.hourly_rate)}</TableCell>
                      <TableCell className="text-right font-mono text-sm font-semibold">{peso(r.gross_pay)}</TableCell>
                      <TableCell className="text-right">
                        <Input type="number" min="0" step="0.01" className="w-24 h-8 ml-auto font-mono text-xs" placeholder="0.00"
                          value={deductions[r.user_id] ?? ""} disabled={r.paid}
                          onChange={(e) => setDeductions({ ...deductions, [r.user_id]: e.target.value })}
                          data-testid={`payroll-deductions-${r.user_id}`} />
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold">{peso(Math.max(net, 0))}</TableCell>
                      <TableCell className="text-right">
                        {r.paid ? (
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-1 text-[10px] font-bold text-emerald-700" data-testid={`payroll-paid-${r.user_id}`}>
                            <CheckCircle2 className="h-3 w-3" /> PAID {peso(r.payout.net_pay)}
                          </span>
                        ) : (
                          <Button size="sm" className="bg-slate-900" disabled={net <= 0} onClick={() => pay(r)} data-testid={`payroll-pay-${r.user_id}`}>
                            <Banknote className="h-3.5 w-3.5 mr-1" /> Pay
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {rows && rows.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="text-center py-12 text-sm text-slate-400">No active staff.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="statement" className="mt-4 space-y-4">
          <div className="flex items-center gap-3">
            <Label className="text-xs">Year</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28 h-9" data-testid="statement-year-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[0, 1, 2].map((d) => {
                  const y = String(new Date().getFullYear() - d);
                  return <SelectItem key={y} value={y}>{y}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          {statement && (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="income-statement-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Income (Net Sales)</TableHead>
                    <TableHead className="text-right">Expenditures</TableHead>
                    <TableHead className="text-right">of which Payroll</TableHead>
                    <TableHead className="text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statement.months.map((m, i) => (
                    <TableRow key={m.month} className={m.income === 0 && m.expenses === 0 ? "opacity-40" : ""}>
                      <TableCell className="font-medium text-slate-900">{MONTH_NAMES[i]} {statement.year}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{peso(m.income)}</TableCell>
                      <TableCell className="text-right font-mono text-sm text-red-600">{peso(m.expenses)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-slate-500">{peso(m.payroll)}</TableCell>
                      <TableCell className={`text-right font-mono text-sm font-semibold ${m.net < 0 ? "text-red-600" : "text-emerald-700"}`}>{peso(m.net)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow className="bg-slate-900 text-white">
                    <TableCell className="font-bold">Yearly Total</TableCell>
                    <TableCell className="text-right font-mono font-bold" data-testid="statement-yearly-income">{peso(statement.totals.income)}</TableCell>
                    <TableCell className="text-right font-mono font-bold" data-testid="statement-yearly-expenses">{peso(statement.totals.expenses)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{peso(statement.totals.payroll)}</TableCell>
                    <TableCell className="text-right font-mono font-bold" data-testid="statement-yearly-net">{peso(statement.totals.net)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
